import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BsdfGlassSockets} from "#material/nodes/shader/bsdf-glass/definition"
import {bsdfGlassDefinition} from "#material/nodes/shader/bsdf-glass/definition"
import type {ThreeEvaluator, CyclesEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {GlassBsdfNode} from "#material/cycles/types"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import {cyclesDistribution} from "#material/nodes/shader/bsdf-glass/helpers"
import {threeConvert, threeNormalFromVec3, threeRGBColor, threeValue} from "#material/three/three-utils"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"

@registerMaterialGraphNode
export class BsdfGlass extends MaterialNode<BsdfGlassSockets> {
    public get definition() {
        return bsdfGlassDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BsdfGlassSockets>> => {
        const material = new ThreeColormassMaterial()

        const {color, roughness, ior, normal} = await evaluator.getAll(this.parameters.inputs)

        material.colorNode = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? material.colorNode
        material.roughnessNode = roughness?.toFloat() ?? threeConvert(this.parameters.settings.roughness, threeValue) ?? material.roughnessNode
        material.metalnessNode = threeValue(0)
        material.iorNode = ior?.toFloat() ?? threeConvert(this.parameters.settings.ior, threeValue) ?? material.iorNode

        material.normalNode =
            normal?.toVec3() ?? (this.parameters.settings.normal ? threeNormalFromVec3(this.parameters.settings.normal) : undefined) ?? material.normalNode

        material.transparent = true
        material.transmissionNode = threeValue(1)
        material.transmission = 0.01
        material.thicknessNode = threeValue(1)

        return {bsdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BsdfGlassSockets>> => {
        const bsdf: GlassBsdfNode = {
            type: "glass_bsdf",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                normal: this.parameters.inputs.normal,
                roughness: this.parameters.inputs.roughness,
                IOR: this.parameters.inputs.ior,
                surface_mix_weight: undefined,
            }),
            parameters: {
                color: cyclesColor(this.parameters.settings.color),
                roughness: this.parameters.settings.roughness,
                normal: cyclesVec3(this.parameters.settings.normal),
                IOR: this.parameters.settings.ior,
                surface_mix_weight: undefined,
                distribution: cyclesDistribution(this.parameters.settings.distribution),
            },
            resources: {},
        }

        return {bsdf: [bsdf, "bsdf"]}
    }
}
